import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import type { StepperItem } from "../enrichment-edit-package/tab-algebra.util";

/**
 * Workflow Stepper that replaces the legacy `<enr-tab>` strip on the
 * enrichment editor shell.
 *
 * Goals (per Phase 2 doc):
 *  - Show a numbered, color-coded sequence so marketing users see progress
 *    instead of an undifferentiated tab strip.
 *  - Hide technical "Advanced" tabs (Product attributes, Custom config)
 *    behind a dropdown so the visible workflow stays short.
 *  - Keep tab id semantics intact so publish-drawer deep-links and other
 *    deep-link callers (preview pencils) continue to work.
 *
 * Stateless beyond the dropdown open state — all data is provided via inputs.
 * Readiness status is reflected via each step's indicator color (driven by the
 * `status` field on StepperItem) rather than a separate badge or popover.
 */
@Component({
  selector: "app-enrichment-edit-package-stepper",
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: "./enrichment-edit-package-stepper.html",
  styleUrls: ["./enrichment-edit-package-stepper.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnrichmentEditPackageStepper {
  readonly steps = input<StepperItem[]>([]);
  readonly advancedSteps = input<StepperItem[]>([]);
  readonly activeId = input<string>("");

  @Output() stepSelect = new EventEmitter<string>();

  private readonly host = inject(ElementRef<HTMLElement>);
  protected readonly dropdownOpen = signal(false);

  /** Whether the active step is hiding in the advanced dropdown. Drives
   *  the dropdown trigger's "active" styling so users always know where
   *  they are. */
  readonly activeIsAdvanced = computed(() =>
    this.advancedSteps().some((s) => s.id === this.activeId()),
  );

  /** Label to surface on the advanced trigger when the active tab lives
   *  inside the dropdown — otherwise just "Advanced". */
  readonly advancedTriggerLabel = computed(() => {
    if (!this.activeIsAdvanced()) return "Advanced";
    const match = this.advancedSteps().find((s) => s.id === this.activeId());
    return match ? `Advanced · ${match.label}` : "Advanced";
  });

  selectStep(id: string): void {
    this.stepSelect.emit(id);
  }

  selectAdvanced(id: string): void {
    this.stepSelect.emit(id);
    this.dropdownOpen.set(false);
  }

  toggleDropdown(): void {
    this.dropdownOpen.update((v) => !v);
  }

  closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  /** Click-outside handler — close the Advanced dropdown when the user
   *  clicks anywhere outside this component's host element. */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    const outsideHost = target ? !this.host.nativeElement.contains(target) : true;
    if (outsideHost && this.dropdownOpen()) {
      this.dropdownOpen.set(false);
    }
  }

  /** Esc closes the Advanced dropdown. */
  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.dropdownOpen()) this.dropdownOpen.set(false);
  }

  trackById = (_: number, item: StepperItem): string => item.id;
}
